import React from 'react';
import { JobDetailsListItem } from '../../jobs/JobDetailsListItem';
import { Box, HStack, Icon, InfoBadge, Text } from '@medely/ui-kit';
import { useAssignmentRequestsForAssignmentGeneralInfo } from './graphql';
import { Link } from '@mp/components/Link';
import { cityZip, couldShowFlexibleDates, fullAddress } from '../utils';
import { COVID_REQUIREMENTS, DisplayHelpers, gateNames } from '@mp';
import { HelpCenterLink } from '@mp/components/helpCenterLink';
import { useGate } from '@mp/hooks/useGate';
import { PostingFieldsGroup } from '@mp/components/postingFieldsGroup/PostingFieldsGroup';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';
import { AssignmentForAssignmentDetailsGql } from '../graphql';

type AssignmentGeneralInfoProps = {
  assignment?: FragmentType<typeof AssignmentForAssignmentDetailsGql> | null;
  smallIcon?: boolean;
};

export const AssignmentGeneralInfo = ({
  smallIcon = false,
  assignment: inputAssignment,
}: AssignmentGeneralInfoProps) => {
  const assignment = parseFragmentData(AssignmentForAssignmentDetailsGql, inputAssignment);
  const { assignmentRequests } = useAssignmentRequestsForAssignmentGeneralInfo({
    assignment_id: assignment?.id,
  });

  const bookedOrInProgress = ['booked', 'in_progress'].includes(assignment?.status ?? '');

  const isNewFacilityGuideFormat = useGate(gateNames.facilityGuide);

  const isAppliedOfferedAccepted = assignmentRequests?.find((req) =>
    ['applied', 'offer_sent', 'accepted'].includes(req.status ?? ''),
  );
  const isInProgress = ['in_progress'].includes(assignment?.status ?? '');
  const showLocationLink = isAppliedOfferedAccepted || isInProgress;

  const covidRequirement =
    assignment?.primary_location?.covid_requirement ||
    assignment?.primary_location?.state?.covid_requirement;

  const showsStipends = assignment?.is_w2 && assignment?.is_travel;

  const location = DisplayHelpers.location.cityStateZip(
    assignment?.primary_location?.address_city,
    assignment?.primary_location?.state?.abbreviation,
    assignment?.primary_location?.address_zip,
  );

  return (
    <>
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-hospital"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title="Location"
        >
          {showLocationLink ? (
            <Link
              url={`https://maps.google.com/?q=${fullAddress(
                assignment?.primary_location?.address_first,
                assignment?.primary_location?.address_second,
              )} ${cityZip(
                assignment?.primary_location?.address_city,
                assignment?.primary_location?.state?.abbreviation,
                assignment?.primary_location?.address_zip,
              )}`}
              variant="text"
              size="m"
            >
              <HStack flexWrap="wrap">
                <Text size="m" color="state.teal.primary">
                  {assignment?.primary_location?.name}
                </Text>
                <Text size="m" color="state.teal.primary">
                  {assignment?.primary_location?.address_first}
                </Text>
                <Text size="m" color="state.teal.primary">
                  {location}
                </Text>
              </HStack>
            </Link>
          ) : (
            <Text size="m" color="state.teal.primary">
              {location}
            </Text>
          )}
          <Text size="m">
            {`Facility Type: ${assignment?.primary_location?.location_type?.label ?? ''}`}
          </Text>
        </JobDetailsListItem>
      </Box>
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-calendar"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title={
            couldShowFlexibleDates(
              assignment?.status ?? '',
              assignment?.flexible_starts_date ?? false,
            )
              ? 'Tentative start date / end date'
              : 'Start date / end date'
          }
        >
          <Text size="m">{`${DisplayHelpers.assignment.assignmentStartDateAndEndDate(
            assignment?.primary_location?.timezone_lookup,
            assignment?.starts_date,
            assignment?.ends_date,
          )}`}</Text>
        </JobDetailsListItem>
      </Box>
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-clock"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title={isNewFacilityGuideFormat ? 'Shift time' : 'Shift'}
        >
          <Text size="m">
            {DisplayHelpers.assignment.time(
              assignment?.primary_location?.timezone_lookup,
              assignment?.starts_time,
              assignment?.ends_time,
              assignment?.number_of_shifts_per_week,
            )}
          </Text>
          {assignment?.variable_shift_times && <Text size="m">Actual shift times may vary</Text>}
        </JobDetailsListItem>
      </Box>
      {assignment?.specialties && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-star-of-life"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Specialties"
          >
            <HStack gap={0.5} flexWrap="wrap" pt={1}>
              {assignment.specialties.map((specialty) => (
                <Box key={specialty?.id} pt={smallIcon ? 0.5 : 0}>
                  <InfoBadge label={specialty?.label || ''} />
                </Box>
              ))}
            </HStack>
          </JobDetailsListItem>
        </Box>
      )}
      {assignment?.patient_population && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-head-side-medical"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title={isNewFacilityGuideFormat ? 'Patients' : 'Patient population'}
          >
            <Text size="m">
              {DisplayHelpers.assignment.patientPopulation(assignment?.patient_population)}
            </Text>
            {isNewFacilityGuideFormat && (
              <PostingFieldsGroup postingFieldContents={assignment?.posting_field_contents} />
            )}
          </JobDetailsListItem>
        </Box>
      )}
      {assignment?.ehr_system && !isNewFacilityGuideFormat && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-window"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="EHR system"
          >
            <Text size="m">{assignment?.ehr_system.label}</Text>
          </JobDetailsListItem>
        </Box>
      )}
      {covidRequirement && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-syringe"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Covid protocol"
          >
            <Text size="m">
              {COVID_REQUIREMENTS.info[covidRequirement as keyof typeof COVID_REQUIREMENTS.info]}
              <HelpCenterLink text=" Learn More." configKey="covidVaccineOrTest" />
            </Text>
          </JobDetailsListItem>
        </Box>
      )}
      {showsStipends && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-house"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Housing/M&amp;IE stipends"
          >
            <Text size="m">Non-taxable income for housing, meals & incidental expenses</Text>
          </JobDetailsListItem>
        </Box>
      )}
      {assignment?.clinical_contact && bookedOrInProgress && isNewFacilityGuideFormat && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-user-doctor"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Clinical contact"
          >
            <Text size="m">
              {assignment.clinical_contact.first_name} {assignment.clinical_contact.last_name}
            </Text>
          </JobDetailsListItem>
        </Box>
      )}
      {isNewFacilityGuideFormat && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-tag"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title={`Assignment ID: ${assignment?.id}`}
          >
            {assignment?.jobs_detail && (
              <Text size="m" color="text.primary" testId="assignment-jobs-details-description">
                {assignment.jobs_detail}
              </Text>
            )}
          </JobDetailsListItem>
        </Box>
      )}
    </>
  );
};
